import React, { FocusEvent, KeyboardEvent, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  FormControl,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Theme,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';

import { useShortcut } from 'utils/hooks/keyboard/useShortcut';
import { getSetting } from 'reducers/configs/settings';
import { KeyboardShortcut } from 'components/KeyboardShortcut';
import { ShortcutAction } from 'constants/shortcuts';

const useStyles = makeStyles<Theme>({
  search: {
    width: '100%',
    borderRadius: '4px',
  },
});

type SearchComponentProps = {
  placeholder?: string;
  searchValue: string | null;
  setSearchValue: (str: string) => void;
  active: boolean;
  shortcutAction?: ShortcutAction;
  close: () => void;
  testId: 'product-search-input' | 'customer-search-input';
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: (event?: FocusEvent<HTMLInputElement>) => void;
};

const SearchComponent = React.forwardRef<
  HTMLInputElement,
  SearchComponentProps
>(
  (
    {
      placeholder = '',
      searchValue,
      setSearchValue,
      active,
      shortcutAction,
      close,
      testId,
      onKeyDown,
      onFocus,
    },
    ref,
  ) => {
    const { t } = useTranslation('search');
    const isDarkTheme = Boolean(
      useSelector(getSetting('touchpos_use_dark_theme')),
    );
    const classes = useStyles({ isDarkTheme });

    useEffect(() => {
      if (active && ref && 'current' in ref && ref.current) {
        ref.current.focus();
      }
    }, [active, ref]);

    useShortcut('cancel', close, 0);
    return (
      <FormControl className={classes.search} variant="outlined">
        <OutlinedInput
          placeholder={t(`${placeholder}.title`)}
          value={searchValue ?? ''}
          data-testid={testId}
          onKeyDown={onKeyDown}
          onFocus={() => onFocus?.()}
          onChange={e => setSearchValue(e.target.value)}
          id={testId}
          inputRef={ref}
          className="pl-3"
          endAdornment={
            active && (
              <InputAdornment position="end">
                <IconButton
                  aria-label="close popover"
                  onClick={e => {
                    e.stopPropagation();
                    close();
                  }}
                >
                  x
                </IconButton>
              </InputAdornment>
            )
          }
        />
        {!active && shortcutAction && (
          <KeyboardShortcut
            onClick={() => {
              if (ref && 'current' in ref) {
                ref.current?.focus();
              }
            }}
            shortcutAction={shortcutAction}
            extraStyles={{ fontSize: '13px', color: '#9fa1a9' }}
          />
        )}
      </FormControl>
    );
  },
);

export default SearchComponent;
